// Pure resolver for the booking confirmation page's bay field. No React, no
// page imports, no side effects — resolveBayPresentation is trivially
// unit-testable and owns all bay-vs-location display logic in one place.

export interface BayPresentationInput {
  // Set only when the member manually picked a bay.
  bayName?: string;
  // Originally-requested primary bay's display name (auto-assign mode only).
  requestedBayName?: string;
  bookingType?: string;
  // Actual assigned room name from the booking response.
  assignedBayName?: string;
}

export interface BayPresentation {
  fieldLabel: 'Bay' | 'Where';
  bayName: string;
  fallbackMessage: string | null;
}

/**
 * Resolve the bay field label, display name, and (when applicable) a
 * retrospective fallback note for the booking confirmation page.
 *
 * Display-name precedence: assignedBayName -> bayName -> requestedBayName ->
 * bookingType -> ''.
 *
 * A fallback note is only ever shown for auto-assign bookings (bayName absent)
 * where both the requested and assigned bay are known and they differ. Manual
 * bay picks (bayName present) never surface a fallback note — the server-side
 * retry loop never engages for manual picks, so any mismatch cannot be
 * honestly explained as a fallback.
 */
export function resolveBayPresentation(input: BayPresentationInput): BayPresentation {
  const { bayName, requestedBayName, bookingType, assignedBayName } = input;

  const fieldLabel: 'Bay' | 'Where' =
    assignedBayName || bayName || requestedBayName ? 'Bay' : 'Where';

  const resolvedBayName = assignedBayName || bayName || requestedBayName || bookingType || '';

  const fallbackMessage =
    !bayName && requestedBayName && assignedBayName && requestedBayName !== assignedBayName
      ? `${requestedBayName} wasn't available - you're booked into ${assignedBayName} instead.`
      : null;

  return { fieldLabel, bayName: resolvedBayName, fallbackMessage };
}
